<template>
  <b-modal 
    ref="modalImportarXML" 
    id="modal-importar-xml"
    title="Importação de Arquivo (XML/TXT) - Direto para o Estoque"
    size="xl"
    header-bg-variant="primary"
    header-text-variant="white"
    no-close-on-backdrop
    no-close-on-esc
    @hidden="onModalHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="mb-0 text-white">
          <i class="fa fa-file-code-o mr-2"></i>
          Importação de Arquivo (XML/TXT) - Direto para o Estoque
        </h5>
        <b-button size="sm" variant="link" class="text-white" @click="close()">
          <i class="fa fa-times fa-lg"></i>
        </b-button>
      </div>
    </template>

    <b-overlay :show="loading" rounded="sm">
      <template #overlay>
        <div class="text-center p-4">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
          <p class="mt-3">{{ loadingMessage }}</p>
        </div>
      </template>

      <div>
        <b-row class="mb-3">
        <b-col md="6">
          <label class="modal-label-premium">Centro de Estoque *</label>
          <b-form-select
            v-model="centroEstoqueId"
            :options="centrosEstoqueOptions"
            :disabled="centroEstoqueLocked"
            class="form-control"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>Selecione o Centro de Estoque</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col md="3">
          <label class="modal-label-premium">Layout de Importação *</label>
          <b-form-select
            v-model="layoutId"
            :options="layoutsOptions"
            class="form-control"
            :state="!!layoutId"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>Selecione o Layout</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col md="2">
          <label class="modal-label-premium">Código Barras</label>
          <b-form-select
            v-model="opcaoCodigoBarras"
            :options="opcoesCodigoBarras"
            class="form-control"
          >
          </b-form-select>
        </b-col>
        <b-col md="2">
          <label class="modal-label-premium">Modo</label>
          <div class="d-flex align-items-center h-100 pb-2">
            <b-form-checkbox
              v-model="opcaoConferencia"
              switch
              class="font-weight-bold text-primary"
              title="Se marcado, as etiquetas entrarão com status 'Pendente de Conferência'"
            >
              Conferir
            </b-form-checkbox>
          </div>
        </b-col>
        <b-col md="2" class="d-flex align-items-end">
          <b-button 
            variant="info" 
            class="px-3 w-100" 
            @click="selecionarArquivo"
            :disabled="!centroEstoqueId || !layoutId"
          >
            <i class="fa fa-folder-open mr-1"></i>Ler Arquivo
          </b-button>
          <input 
            ref="fileInput" 
            type="file" 
            accept=".xml,.txt" 
            style="display: none" 
            @change="processarArquivoXML"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-card no-body class="shadow-sm border-0 mb-3">
            <b-card-header class="bg-light border-bottom">
              <h6 class="mb-0 font-weight-bold text-dark">
                <i class="fa fa-list mr-2 text-primary"></i>Resumo por Produto
              </h6>
            </b-card-header>
            <b-card-body class="p-0">
              <b-table
                :items="resumo"
                :fields="camposResumo"
                striped
                hover
                small
                responsive
                show-empty
                empty-text="Nenhum produto importado"
                head-variant="light"
              >
                <template #cell(PESO_BRUTO)="data">
                  {{ formatarNumero(data.value) }}
                </template>
                <template #cell(PESO_LIQUIDO)="data">
                  {{ formatarNumero(data.value) }}
                </template>
                <template #cell(COLETADO)="data">
                  <b-badge :variant="data.value > 0 ? 'warning' : 'success'" pill>
                    {{ data.value > 0 ? data.value + ' já coletado(s)' : 'OK' }}
                  </b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-card no-body class="shadow-sm border-0">
            <b-card-header class="bg-light border-bottom">
              <h6 class="mb-0 font-weight-bold text-dark">
                <i class="fa fa-cube mr-2 text-info"></i>Detalhamento das Etiquetas
                <b-badge variant="info" pill class="ml-2">{{ itensXML.length }}</b-badge>
              </h6>
            </b-card-header>
            <b-card-body class="p-0" style="max-height: 400px; overflow-y: auto;">
              <b-table
                :items="itensXML"
                :fields="camposItensXML"
                striped
                hover
                small
                responsive
                show-empty
                empty-text="Nenhuma etiqueta importada"
                head-variant="light"
                :tbody-tr-class="rowClass"
              >
                <template #cell(PESO_BRUTO_XML)="data">
                  {{ formatarNumero(data.value) }}
                </template>
                <template #cell(PESO_LIQUIDO_XML)="data">
                  {{ formatarNumero(data.value) }}
                </template>
                <template #cell(TARA_TOTAL_XML)="data">
                  {{ formatarNumero(data.value) }}
                </template>
                <template #cell(DATA_PRODUCAO_XML)="data">
                  {{ formatarData(data.value) }}
                </template>
                <template #cell(DATA_VALIDADE_XML)="data">
                  {{ formatarData(data.value) }}
                </template>
                <template #cell(OP_COLETADO)="data">
                  <i :class="data.value == '1' ? 'fa fa-check text-warning' : 'fa fa-circle-o text-success'"></i>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      </div>
    </b-overlay>

    <template #modal-footer="{ cancel }">
      <b-button variant="danger" class="px-4" @click="cancel()">
        <i class="fa fa-times mr-2"></i>FECHAR
      </b-button>
      <b-button 
        variant="success" 
        class="px-4 font-weight-bold" 
        @click="confirmarEntrada"
        :disabled="itensXML.length === 0 || loading"
      >
        <i class="fa fa-check-circle mr-2"></i>
        {{ opcaoConferencia ? 'ENVIAR PARA CONFERÊNCIA' : 'CONFIRMAR ENTRADA NO ESTOQUE' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Tools from '@/tools/tools.js'

export default {
  name: 'CompraImportarXML',
  props: {
    compra: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingMessage: '',
      centroEstoqueId: null,
      centroEstoqueLocked: false,
      centrosEstoque: [],
      centrosEstoqueOptions: [],
      opcaoCodigoBarras: 'C',
      opcoesCodigoBarras: [
        { value: 'C', text: 'Completo' },
        { value: 'I', text: 'Ignorar Primeiro Número' }
      ],
      opcaoConferencia: false,
      nomeArquivo: '',
      layoutId: null,
      layoutsDisponiveis: [],
      layoutsOptions: [],
      itensXML: [],
      resumo: [],
      camposResumo: [
        { key: 'PRODUTO_ID_XML', label: 'ID Fornec.', sortable: true },
        { key: 'NOME_PRODUTO_XML', label: 'Produto Fornec.', sortable: true },
        { key: 'PRODUTO_ID', label: 'ID CEC', sortable: true },
        { key: 'NOME_PRODUTO', label: 'Produto CEC', sortable: true },
        { key: 'PECAS', label: 'Peças', sortable: true, class: 'text-right' },
        { key: 'PESO_BRUTO', label: 'Peso Bruto', sortable: true, class: 'text-right' },
        { key: 'PESO_LIQUIDO', label: 'Peso Líquido', sortable: true, class: 'text-right' },
        { key: 'COLETADO', label: 'Status', sortable: true, class: 'text-center' }
      ],
      camposItensXML: [
        { key: 'SEQUENCIA_EMBARQUE_XML', label: 'Seq.', sortable: true, class: 'text-center' },
        { key: 'PRODUTO_ID_XML', label: 'ID Fornec.', sortable: true },
        { key: 'NOME_PRODUTO_XML', label: 'Nome Fornec.', sortable: true },
        { key: 'PRODUTO_ID', label: 'ID Pr', sortable: true },
        { key: 'NOME_PRODUTO', label: 'Produto CEC', sortable: true },
        { key: 'CODIGO_BARRAS_XML', label: 'Código Barras', sortable: true },
        { key: 'NUMERO_SERIE_XML', label: 'Série', sortable: true },
        { key: 'VOLUMES_XML', label: 'Vol.', sortable: true, class: 'text-right' },
        { key: 'PECAS_XML', label: 'Pçs', sortable: true, class: 'text-right' },
        { key: 'PESO_LIQUIDO_XML', label: 'Peso Líq.', sortable: true, class: 'text-right' },
        { key: 'DATA_PRODUCAO_XML', label: 'Dt. Produção', sortable: true, class: 'text-center' },
        { key: 'DATA_VALIDADE_XML', label: 'Dt. Validade', sortable: true, class: 'text-center' },
        { key: 'DIAS_VALIDADE_XML', label: 'Dias Val.', sortable: true, class: 'text-right' },
        { key: 'OP_COLETADO', label: 'Coletado', class: 'text-center' }
      ]
    }
  },
  methods: {
    async abrir() {
      await this.carregarCentrosEstoque()
      
      // Se o pedido já tem centro de estoque, usar ele e bloquear
      if (this.compra.CENTRO_ESTOQUE_ID) {
        this.centroEstoqueId = this.compra.CENTRO_ESTOQUE_ID
        this.centroEstoqueLocked = true
      }
      
      // Mostrar o modal usando o ID do b-modal
      this.$bvModal.show('modal-importar-xml')
      
      this.verificarLayout()
    },
    
    async verificarLayout() {
      try {
        const response = await this.$http.post('/api/protected/layout-importacao/pesquisar', {
          fornecedorId: this.compra.FORNECEDOR_ID,
          ativo: 'S'
        })
        this.layoutsDisponiveis = response.data
        this.layoutsOptions = this.layoutsDisponiveis.map(l => ({
          value: l.ID,
          text: `${l.DESCRICAO} (${l.TIPO_ARQUIVO})`
        }))

        if (this.layoutsDisponiveis.length === 1) {
          this.layoutId = this.layoutsDisponiveis[0].ID
        }
      } catch (error) {
        console.error('Erro ao verificar layout:', error)
      }
    },
    
    async carregarCentrosEstoque() {
      try {
        const response = await this.$http.get('/api/protected/centroEstoque/listartodos')
        this.centrosEstoque = response.data
        
        // Converter para formato do b-form-select
        this.centrosEstoqueOptions = this.centrosEstoque.map(item => ({
          value: item.ID,
          text: item.DESCRICAO
        }))
      } catch (error) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Erro ao carregar centros de estoque')
        console.error(error)
      }
    },
    
    selecionarArquivo() {
      this.$refs.fileInput.click()
    },
    
    async processarArquivoXML(event) {
      const file = event.target.files[0]
      if (!file) return
      
      this.loading = true
      this.loadingMessage = 'Lendo o Arquivo XML...'
      
      try {
        const formData = new FormData()
        formData.append('arquivo', file)
        formData.append('fornecedorId', this.compra.FORNECEDOR_ID)
        formData.append('pedidoCompraId', this.compra.ID)
        formData.append('layoutId', this.layoutId)
        
        const response = await this.$http.post('/api/protected/compra/processar-xml', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.data.sucesso) {
          this.itensXML = response.data.itensXML
          this.resumo = response.data.resumo
          this.nomeArquivo = file.name
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', `XML processado com sucesso! ${this.itensXML.length} etiquetas lidas.`)
        } else {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', response.data.mensagem || 'Erro ao processar XML')
        }
      } catch (error) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', 'Erro ao processar arquivo XML')
        this.tocarSomErro()
        console.error(error)
      } finally {
        this.loading = false
        this.$refs.fileInput.value = ''
      }
    },
    
    async confirmarEntrada() {
      // Validar se há produtos não vinculados
      const produtosNaoVinculados = this.itensXML.filter(item => !item.PRODUTO_ID || item.PRODUTO_ID <= 0)
      if (produtosNaoVinculados.length > 0) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', `Existem ${produtosNaoVinculados.length} produtos não vinculados. Observe os produtos em VERMELHO. Talvez a relação esteja errada no cadastro dos produtos do sistema.`)
        return
      }
      
      // Validar se há etiquetas já coletadas
      const etiquetasColetadas = this.itensXML.filter(item => item.OP_COLETADO == '1')
      if (etiquetasColetadas.length > 0) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', `Existem ${etiquetasColetadas.length} etiquetas que já estão no estoque. Observe a coluna COLETADO.`)
        return
      }
      
      const mensagem = this.opcaoConferencia 
        ? 'Ao confirmar, as etiquetas serão enviadas para conferência. Confirmar?'
        : 'Ao Confirmar a Importação, todas as Etiquetas entrarão diretamente no Estoque. Confirmar?'

      const confirmacao = await this.$bvModal.msgBoxConfirm(
        mensagem,
        {
          title: 'Confirmar Importação',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'SIM',
          cancelTitle: 'NÃO',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      
      if (!confirmacao) return
      
      this.loading = true
      this.loadingMessage = 'Registrando as Etiquetas no Estoque...'
      
      try {
        const response = await this.$http.post('/api/protected/compra/confirmar-importacao-xml', {
          pedidoCompraId: this.compra.ID,
          centroEstoqueId: this.centroEstoqueId,
          itensXML: this.itensXML,
          resumo: this.resumo,
          nomeArquivo: this.nomeArquivo,
          opcaoCodigoBarras: this.opcaoCodigoBarras,
          opcaoConferencia: this.opcaoConferencia,
          login: window.localStorage.getItem('login')
        })
        
        if (response.data.sucesso) {
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Todas as Etiquetas foram Registradas no Estoque')
          
          this.$bvModal.hide('modal-importar-xml')
          this.$emit('importacao-concluida')
        } else {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', response.data.mensagem || 'Erro ao confirmar importação')
        }
      } catch (error) {
        Tools.exibirMensagem(this.$store, 'Falha', 'danger', 'FALHA! Procure um técnico da TI')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    onModalHidden() {
      this.itensXML = []
      this.resumo = []
      this.nomeArquivo = ''
      if (!this.compra.CENTRO_ESTOQUE_ID) {
        this.centroEstoqueId = null
        this.centroEstoqueLocked = false
      }
    },
    
    rowClass(item, type) {
      if (!item || type !== 'row') return
      
      if (!item.PRODUTO_ID || item.PRODUTO_ID <= 0) {
        return 'table-danger'
      }
      
      if (item.OP_COLETADO == '1') {
        return 'table-warning'
      }
      
      return 'table-success'
    },
    
    formatarNumero(valor) {
      if (!valor && valor !== 0) return '0,00'
      return parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    
    formatarData(valor) {
      if (!valor) return ''
      const data = new Date(valor)
      return data.toLocaleDateString('pt-BR')
    }
  }
}
</script>

<style scoped>
.modal-label-premium {
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

::v-deep .table-danger {
  background-color: #f8d7da !important;
  color: #721c24;
}

::v-deep .table-warning {
  background-color: #fff3cd !important;
  color: #856404;
}

::v-deep .table-success {
  background-color: #d4edda !important;
  color: #155724;
}
</style>



// WEBPACK FOOTER //
// src/modals/CompraImportarXML.vue